import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import BookRide from '../components/BookRide';
import Footer from '../components/Footer';
import './Narsimha.css';

const quickSpecs = [
  { label: 'Displacement', value: '976cc' },
  { label: 'Fuel Supply', value: 'Delphi EFI' },
  { label: 'Maximum Horse Power', value: '92/7300 HP/RPM' },
  { label: 'Max. Torque', value: '95/5500 Nm/RPM' },
  { label: 'Payload Capacity', value: '300 kg' },
];

const powerCards = [
  {
    title: 'Precision Engineering',
    text: 'Powered by a 976 cc V-Twin engine pushing 68 HP and 95 Kw torque, the Narasimha ATV conquers steep inclines, deep mud, and tough obstacles with ease.',
  },
  {
    title: 'Built for Durability and Safety',
    text: "Narasimha's rugged chassis, reinforced with a full metal skid plate and hydraulic disc brakes, shrugs off heavy impacts — giving you total control in tough terrains.",
  },
  {
    title: 'Low Maintenance with Customisation Options',
    text: 'With a fast supply chain for quick replacements, we keep maintenance low and downtime minimal. Plus, customise it your way so that every detail is made just for you.',
  },
];

const detailCards = [
  {
    title: 'Engine',
    text: 'Powered by a 976cc 4-Stroke V-Twin engine, delivering 95 Nm of torque, our ATV operates efficiently in extreme temperatures (-30°C to 60°C). The DELPHI EFI system ensures optimal fuel management, making it reliable across diverse terrains.',
  },
  {
    title: 'Weight-Bearing Capacity',
    text: 'Designed for heavy loads, it features a front rack capacity of 35 kg, rear rack capacity of 75 kg, and an overall payload of 300 kg. The factory-fitted 1600 kg winch, with a durable synthetic rope, ensures reliable performance for demanding applications.',
  },
  {
    title: 'Terrain Capability',
    text: 'With a 283 mm ground clearance and a 1493 mm wheelbase, the ATV excels in maneuverability. It handles steep 45-degree inclines, side slopes of 33 degrees (unloaded), and has a 1650 mm minimum turning radius for optimal control.',
  },
  {
    title: 'Safety',
    text: 'Designed with a fully factory-fitted roll cage for stability and protection, the ATV also offers a seat harness system for rear passengers. These features ensure a secure and controlled off-road experience.',
  },
  {
    title: 'Drive Train',
    text: 'Featuring selectable 2WD, 4WD, and Lock 4WD with a Visco-4Lok front differential, the ATV uses CV Tech Technology for smooth transitions. The automatic CVT transmission offers Park, Reverse, Neutral, Low, and High modes, enhancing control across challenging terrains.',
  },
  {
    title: 'Warranty',
    text: 'The ATV comes with a standard 3-year factory warranty, covering manufacturing defects. An optional 3-year extended warranty provides additional protection, ensuring long-term durability and peace of mind.',
  },
  {
    title: 'Custom Roll Cage & Cargo Solutions',
    text: 'Factory-fitted roll cages ensure safety during cargo transport. Additional features include detachable roll cage, rear cargo mounts, and customizable canopies made from heavy-duty vinyl, polyester, nylon, or TPU for weather-resistant protection.',
  },
  {
    title: 'Maintenance & Serviceability',
    text: 'Acriolis ATVs require minimal maintenance, with easily accessible components for quick inspections and repairs. High-quality materials reduce wear, and readily available spare parts keep servicing cost-effective and hassle-free.',
  },
  {
    title: 'Material Excellence',
    text: 'Our ATV chassis is built with IS 9130 Chromoly Steel, ensuring a high strength-to-weight ratio, fatigue resistance, and impact durability. With a thickness of 3-3.5mm, it boasts a tensile strength of 560-700 MPa and excellent weldability for a reliable frame.',
  },
  {
    title: 'Customization',
    text: 'Acriolis offers tailored factory-fitted modifications. Submit your design or consult our experts to create a vehicle that meets your specific requirements, ensuring it aligns perfectly with your needs.',
  },
  {
    title: 'Suspension',
    text: 'Equipped with DWB & TTI suspensions, the ATV offers a 35% increase in travel and a 17% tighter turning radius. With a maximum travel rate of 457 mm and shock absorber dampening up to 78 mm, gas shock absorbers further enhance ride comfort.',
  },
  {
    title: 'Accessories & Personalization',
    text: 'We offer a wide range of accessories to enhance your ATV experience. If you have a unique requirement, we provide custom fabrication, ensuring your vehicle meets your exact performance and utility needs.',
  },
];

function Narsimha() {
  return (
    <div>
      <Header />

      {/* ── Hero ── */}
      <section
        className="nar-hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/Rectangle-4913.webp)` }}
      >
        <div className="nar-hero__content">
          <h1 className="nar-hero__heading">
            Customize your ride — as every adventure is unique!
          </h1>
          <a href="#configure" className="nar-hero__btn">
            Configure your product <span>→</span>
          </a>
        </div>
      </section>

      {/* ── Quick specs bar ── */}
      <section className="nar-specs">
        {quickSpecs.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className="nar-specs__item">
              <span className="nar-specs__label">{s.label}</span>
              <span className="nar-specs__value">{s.value}</span>
            </div>
            {i < quickSpecs.length - 1 && <div className="nar-specs__divider" />}
          </React.Fragment>
        ))}
      </section>

      {/* ── Power. Precision. Engineering. ── */}
      <section className="nar-power">
        <span className="nar-power__eyebrow">ALL THAT POWER IN ONE PLACE</span>
        <h2 className="nar-power__heading">Power. Precision. Engineering.</h2>
        <div className="nar-power__grid">
          {powerCards.map((c) => (
            <div className="nar-power__card" key={c.title}>
              <h3 className="nar-power__card-title">{c.title}</h3>
              <p className="nar-power__card-text">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 360 experience ── */}
      <section
        className="nar-360"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/Rectangle-4925.webp)` }}
      >
        <div className="nar-360__overlay" />
        <h2 className="nar-360__heading">Experience in 360°</h2>
      </section>

      {/* ── Detail cards ── */}
      <section className="nar-details" id="configure">
        <h2 className="nar-details__heading">Customise Your Power, Your Way!</h2>
        <div className="nar-details__grid">
          {detailCards.map((c) => (
            <div className="nar-details__card" key={c.title}>
              <h3 className="nar-details__card-title">{c.title}</h3>
              <p className="nar-details__card-text">{c.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Specs CTA ── */}
      <section className="nar-cta">
        <span className="nar-cta__eyebrow">UNLOCK THE POWER: NARASIMHA SPECIFICATIONS</span>
        <h2 className="nar-cta__heading">
          Command the Trail with Narasimha's Ultimate Specs
        </h2>
        <p className="nar-cta__text">
          Tell us how you envision your ideal ATV, and we will work with you to develop it!
          At Acriolis, we are committed to creating personalized solutions tailored to your
          specific needs and preferences. Whether you want unique accessories, specific
          features, or specialized configurations, our team will collaborate with you to
          bring your vision to life.
        </p>
        <Link to="/contact" className="nar-cta__btn">
          Configure your product <span>→</span>
        </Link>
      </section>

      <BookRide />
      <Footer />
    </div>
  );
}

export default Narsimha;
